import React, { useEffect, useRef, useState } from "react";
// 1. Import Splide components
import { Splide, SplideSlide } from "@splidejs/react-splide";
// 2. Import Splide core CSS (or your desired theme)
import "@splidejs/splide/dist/css/splide.min.css";
import Button from "../../components/ui/Button"; // Assuming Button is a common component
import { Play, Pause } from "lucide-react"; // Optional: Icons ke liye
// Import GSAP and ScrollTrigger
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * A reusable section component featuring a header (title, paragraph, button)
 * and a Splide-based carousel/slider with GSAP scroll animations.
 *
 * @param {object} props - Component properties.
 * @param {object} props.header - Contains content for the header.
 * @param {string} props.header.title - The main title text.
 * @param {string} props.header.description - The description paragraph text.
 * @param {object} props.header.button - Optional button properties.
 * @param {string} props.header.button.text - Button text.
 * @param {function} props.header.button.onClick - Button click handler.
 * @param {object[]} props.slides - Array of slide objects for the carousel.
 * @param {string} props.slides[].image - The image source URL.
 * @param {string} props.slides[].alt - The image alt text.
 * @param {object} props.splideOptions - Options to override the default Splide settings.
 */
const ReusableSection = ({
  header,
  slides,
  imageSrc,
  splideOptions: customSplideOptions,
}) => {
  // 1. Create refs for the section and its parts
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const sliderRef = useRef(null);

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Check if source is a video
  const isVideo = imageSrc?.match(/\.(mp4|webm|ogg|mov)$/i);

  // Play/Pause Toggle Function
  const handlePlay = () => {
    setIsPlaying(true);
    // Video ko auto-play karne ke liye thoda delay
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play();
      }
    }, 100);
  };
  // --- GSAP Animation Logic ---
  useEffect(() => {
    // Set up GSAP context for better cleanup
    const context = gsap.context(() => {
      // A. Header and Description Text Animation (Staggered Reveal)
      const headerElements = gsap.utils.toArray(
        headerRef.current.querySelectorAll(".section-header-item"),
      );

      headerElements.forEach((element, index) => {
        // Initial State: Hidden
        gsap.set(element, {
          y: 20,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: headerRef.current,
            start: "top 85%", // Animation starts when header hits 85%
            end: "center 60%",
            scrub: 1, // Smooth, scroll-dependent animation
            // markers: true,
          },
        });

        tl.to(element, {
          y: 0,
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          delay: index * 0.1, // Stagger effect
        });
      });

      // B. Slider Container Animation (Fade In)
      if (sliderRef.current) {
        gsap.from(sliderRef.current, {
          opacity: 0,
          y: 50, // Slight movement up
          duration: 1.5,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sliderRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
            // markers: true,
          },
        });
      }
    }, sectionRef);

    // Cleanup
    return () => context.revert();
  }, []); // Empty dependency array means it runs once on mount

  // --- Default Splide Options ---
  const defaultSplideOptions = {
    type: "loop",
    perPage: 1,
    focus: "center",
    autoplay: true,
    interval: 5000,
    flickMaxPages: 1,
    updateOnMove: true,
    pagination: false,
    padding: "10%",
    throttle: 300,
    arrows: true,
    breakpoints: {
      1440: {
        perPage: 1,
        padding: "30%",
      },
      768: {
        perPage: 1,
        padding: "5%",
        gap: "1rem",
      },
    },
  };

  // Merge default and custom options
  const finalSplideOptions = {
    ...defaultSplideOptions,
    ...customSplideOptions,
  };

  return (
    <section
      ref={sectionRef} // Attach section ref
      className="w-full py-[20px] sm:py-[25px] xl:py-[34px] mt-[50px]"
    >
      <div className="w-full max-w-[1620px] mx-auto px-4 sm:px-6 xl:px-[56px]">
        {/* Section Header - Attach header ref */}

        <div
          ref={headerRef}
          className="text-center mb-[30px] sm:mb-[40px] xl:mb-[50px] space-y-4 xl:space-y-[20px]"
        >
          {/* Title - Uses section-header-item class for GSAP */}
          <h2 className="section-header-item text-[32px] sm:text-[48px] xl:text-xl font-bold leading-[40px] sm:leading-[60px] xl:leading-mega text-secondary-text font-['Parkinsans']">
            {header.title}
          </h2>

          {/* Description - Uses section-header-item class for GSAP */}
          <p className="section-header-item text-[20px] xl:text-sm font-light leading-loose text-primary-text max-w-4xl mx-auto">
            {header.description}
          </p>

          {/* Button - Uses section-header-item class for GSAP */}
          {header.button && (
            <div className="section-header-item">
              <Button
                text={header.button.text}
                onClick={header.button.onClick}
                text_font_size="text-[16px] sm:text-[20px] xl:text-[24px]"
                padding="t=12px,r=32px,b=12px,l=32px"
                text_font_family="Parkinsans"
                text_font_weight="font-semibold"
                text_line_height="leading-normal"
                text_text_align="center"
                // text_color="#1c233a"
                border_border="1px solid #1c233a"
                border_border_radius="rounded-xl"
                layout_width="auto"
                className="hover:bg-secondary-background hover:text-white text-[#1c233a] transition-colors duration-300"
              />
            </div>
          )}
        </div>

        {/* Product Slider Container (using Splide) - Attach slider ref */}
        <div ref={sliderRef} className="mb-[40px] sm:mb-[60px] xl:mb-[80px]">
          <Splide
            options={finalSplideOptions}
            aria-label="Carousel"
            id="splide"
          >
            {slides?.map((slide, index) => (
              <SplideSlide key={index}>
                <div className="flex justify-center">
                  {/* Slide Content */}
                  <div className="w-full rounded-full bg-white flex items-center justify-center">
                    {!isPlaying ? (
                      <>
                        {/* Image Layer */}
                        <img
                          src={slide.image}
                          alt={slide.alt}
                          className="w-full h-full object-cover"
                        />
                        {/* Play Button Overlay */}
                        <button
                          onClick={handlePlay}
                          className="absolute inset-0 flex items-center justify-center  transition-all group"
                        >
                          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="#1c233a"
                              className="w-8 h-8 sm:w-10 sm:h-10 ml-1"
                            >
                              <path d="M8 5.14v14l11-7-11-7z" />
                            </svg>
                          </div>
                        </button>
                      </>
                    ) : (
                      /* Video Layer - Image ki jagah ye dikhega */
                      <div>
                        {/* <video
                        ref={videoRef}
                        src={slide.videoUrl}
                        controls
                        className="w-full h-full object-cover"
                        onPause={() => setIsPlaying(false)}
                          /> */}
                        <img
                          src={slide.image}
                          alt={slide.alt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                  {/* Add more slide content here if needed (e.g., testimonial text, name) */}
                </div>
              </SplideSlide>
            ))}
          </Splide>
        </div>
      </div>
    </section>
  );
};

export default ReusableSection;
